/**
 * mediaUrl.ts — Absolute URL builder for video/photo media.
 *
 * Problem: In the Capacitor iOS shell, WKWebView serves the React bundle from
 * a non-standard origin (capacitor://localhost, ionic://localhost, or a local
 * HTTP server on a random port depending on Capacitor version and config).
 * Relative paths like /api/... or /hls/... resolve against that local origin
 * and fail — the real API server is at https://www.slowrib.com.
 *
 * Fix: detect the Capacitor native shell using Capacitor's own injected global
 * (window.Capacitor.isNativePlatform), which is 100% reliable regardless of
 * which URL scheme or port Capacitor happens to use. Fall back to URL-based
 * heuristics for environments where the global hasn't loaded yet.
 *
 * In a normal browser the function is a no-op — relative URLs already resolve
 * correctly against the page origin (www.slowrib.com).
 */

const SERVER_URL = 'https://www.slowrib.com';

/** True when running inside a Capacitor native shell (iOS / Android). */
function isCapacitorNative(): boolean {
  // Primary: Capacitor injects window.Capacitor into WKWebView before any JS
  // runs. isNativePlatform() returns true on iOS and Android regardless of the
  // URL scheme in use (capacitor://, ionic://, or a local HTTP server port).
  try {
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) return true;
    // Older Capacitor shape — simple boolean
    if (cap?.isNative === true) return true;
  } catch { /* ignore — window.Capacitor not yet available */ }

  // Fallback: URL scheme checks for environments where the global is absent.
  const { protocol, hostname, port } = window.location;
  if (protocol === 'capacitor:' || protocol === 'ionic:') return true;
  // Local server with no port (https://localhost) — Capacitor iosScheme override.
  // The CRA dev server always uses a numbered port, so this is unambiguous.
  if (hostname === 'localhost' && port === '') return true;

  return false;
}

/**
 * Returns an absolute URL suitable for use in <video src>, HLS.js loadSource(),
 * or any other media consumer.
 *
 * Rules:
 *   - Already absolute (http/https)  → returned as-is everywhere
 *   - Relative path (/api/…, /hls/…) → prepend SERVER_URL in Capacitor,
 *                                       return unchanged in browser
 */
export function mediaUrl(filePath: string | null | undefined): string {
  if (!filePath) return '';

  // Already a full URL — safe everywhere.
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Browser — relative paths resolve correctly against the page origin.
  if (!isCapacitorNative()) {
    return filePath;
  }

  // Capacitor native shell — prefix with the real server so WKWebView can
  // reach it instead of resolving against capacitor://localhost.
  const path = filePath.startsWith('/') ? filePath : `/${filePath}`;
  return `${SERVER_URL}${path}`;
}
